import { useMemo, useState } from "react";
import {
  ImageSquare, ArrowSquareOut, DownloadSimple, InstagramLogo, FilmSlate,
  Broadcast, PaintBrush, UserFocus, Confetti, ChatCircleDots, Sparkle,
} from "@phosphor-icons/react";
import CollapsibleSection from "@/components/admin/CollapsibleSection";

/**
 * Admin · Media Library.
 *
 * Compact grid of every Sweat With Slay / Legacy media asset that already
 * exists in /app/frontend/public. Nothing is regenerated, moved, or renamed —
 * each tile links straight to the existing file. Categorised via chip filter.
 *
 * Excludes app-icons, favicons, node_modules, cache, build/dist files, and any
 * unrelated app assets (e.g. index.html chunks).
 */

// ── Asset catalog (static, sourced from existing /public directory) ──────────
// Category  → [{ name, url, participant, created_at (optional) }]
// Types inferred from URL extension.

const IG_FEED_LEGACY = [
  { name: "IG Hero — Sweat for Sacrifice", url: "/assets/press/ig-01-hero.png" },
  { name: "IG — Timmy Goss tribute", url: "/assets/press/ig-02-timmy.png", participant: "Tim Goss" },
  { name: "IG — Harry D tribute", url: "/assets/press/ig-03-harry.png", participant: "Harry D" },
  { name: "IG — 911-minute challenge", url: "/assets/press/ig-04-911.png" },
  { name: "IG — Movement", url: "/assets/press/ig-05-movement.png" },
  { name: "IG — Donate", url: "/assets/press/ig-06-donate.png" },
  { name: "IG — Legacy Wall", url: "/assets/press/ig-07-wall.png" },
];

const IG_STORIES_LEGACY = [
  { name: "Story — Hero", url: "/assets/press/story-01-hero.png" },
  { name: "Story — Timmy", url: "/assets/press/story-02-timmy.png", participant: "Tim Goss" },
  { name: "Story — CTA", url: "/assets/press/story-03-cta.png" },
];

const REELS_LEGACY = [
  { name: "Reel — Who is AJ", url: "/assets/press/reel-01-who.mp4", participant: "AJ (SlayJ)" },
  { name: "Reel — Timmy tribute", url: "/assets/press/reel-02-timmy.mp4", participant: "Tim Goss" },
  { name: "Reel — 911-minute challenge", url: "/assets/press/reel-03-challenge.mp4" },
];

const IG_CAROUSEL_SWS = [
  ...Array.from({ length: 7 }, (_, i) => ({ name: `Carousel · Day ${i + 1}`, url: `/brand/c_day_${i + 1}.png` })),
  ...Array.from({ length: 6 }, (_, i) => ({ name: `Carousel · FAQ ${i + 1}`, url: `/brand/c_faq_${i + 1}.png` })),
  ...Array.from({ length: 5 }, (_, i) => ({ name: `Carousel · Pick your plan ${i + 1}`, url: `/brand/c_pick_${i + 1}.png` })),
  ...Array.from({ length: 3 }, (_, i) => ({ name: `Carousel · Corporate/PD ${i + 1}`, url: `/brand/c_cpd_${i + 1}.png` })),
  ...Array.from({ length: 3 }, (_, i) => ({ name: `Carousel · Relaunch ${i + 1}`, url: `/brand/c_relaunch_${i + 1}.png` })),
];

const IG_STORIES_SWS = [
  { name: "Story · About 1", url: "/brand/s_about_1.png" },
  { name: "Story · About 2", url: "/brand/s_about_2.png" },
  { name: "Story · About CTA", url: "/brand/s_about_cta.png" },
  { name: "Story · Everfit 1", url: "/brand/s_everfit_1.png" },
  { name: "Story · Everfit 2", url: "/brand/s_everfit_2.png" },
  { name: "Story · Everfit CTA", url: "/brand/s_everfit_cta.png" },
  { name: "Story · Fuel 1", url: "/brand/s_fuel_1.png" },
  { name: "Story · Fuel 2", url: "/brand/s_fuel_2.png" },
  { name: "Story · Fuel CTA", url: "/brand/s_fuel_cta.png" },
  { name: "Story · Intake 1", url: "/brand/s_intake_1.png" },
  { name: "Story · Intake 2", url: "/brand/s_intake_2.png" },
  { name: "Story · Intake CTA", url: "/brand/s_intake_cta.png" },
  { name: "Story · Notes 1", url: "/brand/s_notes_1.png" },
  { name: "Story · Notes 2", url: "/brand/s_notes_2.png" },
  { name: "Story · Notes CTA", url: "/brand/s_notes_cta.png" },
  { name: "Story · Programs 1", url: "/brand/s_programs_1.png" },
  { name: "Story · Programs 2", url: "/brand/s_programs_2.png" },
  { name: "Story · Programs CTA", url: "/brand/s_programs_cta.png" },
  { name: "Story · Travel 1", url: "/brand/s_travel_1.png" },
  { name: "Story · Travel 2", url: "/brand/s_travel_2.png" },
  { name: "Story · Travel CTA", url: "/brand/s_travel_cta.png" },
  { name: "Story · Wins 1", url: "/brand/s_wins_1.png" },
  { name: "Story · Wins 2", url: "/brand/s_wins_2.png" },
  { name: "Story · Wins CTA", url: "/brand/s_wins_cta.png" },
  { name: "Story · Box tribute", url: "/brand/s_box_story.png" },
  { name: "Story · Relaunch", url: "/brand/s_relaunch.png" },
  { name: "Story · Prog Essentials", url: "/brand/s_prog_essentials.png" },
  { name: "Story · Prog Coaching", url: "/brand/s_prog_coaching.png" },
  { name: "Story · Prog VIP", url: "/brand/s_prog_vip.png" },
  { name: "Story · Prog Bespoke", url: "/brand/s_prog_bespoke.png" },
];

const DAY_OF_WEEK = [
  { name: "DOW · Macro Monday", url: "/brand/s_dow_macro_mon.png" },
  { name: "DOW · Tip Tuesday", url: "/brand/s_dow_tip_tue.png" },
  { name: "DOW · Wins Wednesday", url: "/brand/s_dow_wins_wed.png" },
  { name: "DOW · Lift Thursday", url: "/brand/s_dow_lift_thu.png" },
  { name: "DOW · Friday Fuel", url: "/brand/s_dow_friday_fuel.png" },
  { name: "DOW · Saturday Sweat", url: "/brand/s_dow_sat_sweat.png" },
  { name: "DOW · Self Sunday", url: "/brand/s_dow_self_sun.png" },
  { name: "DOW · Ask Me", url: "/brand/s_dow_ask_me.png" },
];

const STORY_STICKERS = [
  { name: "Sticker · Poll", url: "/brand/s_stk_poll.png" },
  { name: "Sticker · Quiz", url: "/brand/s_stk_quiz.png" },
  { name: "Sticker · Question", url: "/brand/s_stk_question.png" },
  { name: "Sticker · Emoji", url: "/brand/s_stk_emoji.png" },
];

const HIGHLIGHTS = [
  { name: "Highlight · About", url: "/brand/hl_about.png" },
  { name: "Highlight · Everfit", url: "/brand/hl_everfit.png" },
  { name: "Highlight · Fuel", url: "/brand/hl_fuel.png" },
  { name: "Highlight · Intake", url: "/brand/hl_intake.png" },
  { name: "Highlight · Notes", url: "/brand/hl_notes.png" },
  { name: "Highlight · Programs", url: "/brand/hl_programs.png" },
  { name: "Highlight · Travel", url: "/brand/hl_travel.png" },
  { name: "Highlight · Wins", url: "/brand/hl_wins.png" },
];

const BRAND_GRAPHICS = [
  { name: "Add-ons hero", url: "/brand/g_addons.png" },
  { name: "Before/After template", url: "/brand/g_ba_template.png" },
  { name: "Box · Program", url: "/brand/g_box_program.png" },
  { name: "Box · Quote", url: "/brand/g_box_quote.png" },
  { name: "Box · V1", url: "/brand/g_box_v1.png" },
  { name: "Box · V2", url: "/brand/g_box_v2.png" },
  { name: "Box · V3", url: "/brand/g_box_v3.png" },
  { name: "Box · V4", url: "/brand/g_box_v4.png" },
  { name: "Box · V5", url: "/brand/g_box_v5.png" },
  { name: "Boxing tribute story", url: "/brand/g_boxing_story.png" },
  { name: "BTS", url: "/brand/g_bts.png" },
  { name: "Carousel banner", url: "/brand/g_carousel.png" },
  { name: "Compare plans", url: "/brand/g_compare.png" },
  { name: "DJ Coach sales", url: "/brand/g_dj_coach_sales.png" },
  { name: "DJ Mix offer", url: "/brand/g_djmix_offer.png" },
  { name: "Email signature", url: "/brand/g_email_sig.png" },
  { name: "Headline hero", url: "/brand/g_headline.png" },
  { name: "Linktree cover", url: "/brand/g_linktree.png" },
  { name: "PDF · 14 days", url: "/brand/g_pdf_14days.png" },
  { name: "PDF · Busy pros", url: "/brand/g_pdf_busy.png" },
  { name: "PDF · Hotel workout", url: "/brand/g_pdf_hotel.png" },
  { name: "Program · Bespoke", url: "/brand/g_prog_bespoke.png" },
  { name: "Program · Coaching", url: "/brand/g_prog_coaching.png" },
  { name: "Program · Essentials", url: "/brand/g_prog_essentials.png" },
  { name: "Program · VIP", url: "/brand/g_prog_vip.png" },
  { name: "Program tile", url: "/brand/g_program.png" },
  { name: "Quote graphic", url: "/brand/g_quote.png" },
  { name: "Relaunch cover", url: "/brand/g_relaunch.png" },
  { name: "Vs a Coach", url: "/brand/g_vs_coach.png" },
  { name: "Win · 1", url: "/brand/g_win_1.png" },
  { name: "Win · 2", url: "/brand/g_win_2.png" },
  { name: "Win · 3", url: "/brand/g_win_3.png" },
  { name: "Win · 4", url: "/brand/g_win_4.png" },
];

const REELS_SWS = [
  { name: "Reel Frame 1", url: "/brand/s_reel_1.png" },
  { name: "Reel Frame 2", url: "/brand/s_reel_2.png" },
  { name: "Reel Frame 3", url: "/brand/s_reel_3.png" },
  { name: "Reel Frame 4", url: "/brand/s_reel_4.png" },
  { name: "Reel Frame 5", url: "/brand/s_reel_5.png" },
];

const PROMO_VIDEOS = Array.from({ length: 28 }, (_, i) => {
  const n = i + 1;
  return { name: `Promo #${String(n).padStart(2, "0")}`, url: n === 1 ? "/sws-promo.mp4" : `/sws-promo-${n}.mp4` };
});

const MOVING_FOR_TEMPLATES = [
  { name: "I'm Moving For · template", url: "/legacy-share/moving-for-template.png" },
];

const CATEGORIES = [
  { id: "brand", label: "Brand Graphics", icon: PaintBrush, items: BRAND_GRAPHICS, type: "img" },
  { id: "ig-carousel", label: "IG Carousels", icon: InstagramLogo, items: IG_CAROUSEL_SWS, type: "img" },
  { id: "ig-feed-legacy", label: "IG Feed (Legacy)", icon: InstagramLogo, items: IG_FEED_LEGACY, type: "img" },
  { id: "ig-stories-legacy", label: "IG Stories (Legacy)", icon: Broadcast, items: IG_STORIES_LEGACY, type: "img" },
  { id: "ig-stories-sws", label: "IG Stories (SWS)", icon: Broadcast, items: IG_STORIES_SWS, type: "img" },
  { id: "dow", label: "Day-of-Week Templates", icon: Confetti, items: DAY_OF_WEEK, type: "img" },
  { id: "stickers", label: "Story Stickers", icon: ChatCircleDots, items: STORY_STICKERS, type: "img" },
  { id: "highlights", label: "IG Highlight Covers", icon: Sparkle, items: HIGHLIGHTS, type: "img" },
  { id: "reels-legacy", label: "Reels (Legacy)", icon: FilmSlate, items: REELS_LEGACY, type: "video" },
  { id: "reels-sws", label: "Reel Frames (SWS)", icon: FilmSlate, items: REELS_SWS, type: "img" },
  { id: "promo", label: "Promo Videos", icon: FilmSlate, items: PROMO_VIDEOS, type: "video" },
  { id: "moving-for", label: "I'm Moving For", icon: UserFocus, items: MOVING_FOR_TEMPLATES, type: "img" },
];

// ─────────────────────────────────────────────────────────────────────────────

function AssetTile({ item, type }) {
  const filename = item.url.split("/").pop();
  return (
    <div data-testid={`media-tile-${filename}`} className="border border-white/10 bg-black flex flex-col">
      <div className="aspect-square bg-[#0F0F0F] overflow-hidden">
        {type === "video" ? (
          <video src={item.url} preload="metadata" controls className="w-full h-full object-contain bg-black" />
        ) : (
          <img src={item.url} alt={item.name} loading="lazy" className="w-full h-full object-cover" />
        )}
      </div>
      <div className="p-2 space-y-1.5">
        <p className="text-white text-[11px] font-bold leading-tight line-clamp-2" title={item.name}>{item.name}</p>
        <p className="text-white/40 text-[9px] font-mono truncate" title={item.url}>{filename}</p>
        {item.participant && (
          <p className="text-[#FF1B91] text-[9px] uppercase tracking-[0.2em] font-bold">{item.participant}</p>
        )}
        <div className="flex items-center gap-1.5 pt-1">
          <a
            href={item.url}
            target="_blank"
            rel="noreferrer noopener"
            data-testid={`media-open-${filename}`}
            className="flex-1 inline-flex items-center justify-center gap-1 text-[9px] uppercase tracking-[0.2em] font-bold border border-white/20 hover:border-[#FF1B91] text-white/80 hover:text-white px-2 py-1.5"
          >
            <ArrowSquareOut size={10} weight="bold" /> Open
          </a>
          <a
            href={item.url}
            download
            data-testid={`media-dl-${filename}`}
            className="flex-1 inline-flex items-center justify-center gap-1 text-[9px] uppercase tracking-[0.2em] font-bold bg-[#FF1B91] hover:bg-[#FF3FA3] text-white px-2 py-1.5"
          >
            <DownloadSimple size={10} weight="bold" /> DL
          </a>
        </div>
      </div>
    </div>
  );
}

export default function MediaLibraryPanel() {
  const [activeId, setActiveId] = useState("all");
  const [nonce, setNonce] = useState(0); // bumped by Refresh to re-run useMemo

  const visible = useMemo(() => {
    // touching `nonce` forces the memo to recompute on Refresh
    if (nonce < 0) return [];
    if (activeId === "all") return CATEGORIES;
    return CATEGORIES.filter((c) => c.id === activeId);
  }, [activeId, nonce]);

  const totalCount = useMemo(() => CATEGORIES.reduce((n, c) => n + c.items.length, 0), []);

  // Force React to re-mount the child <img>/<video> nodes so newly-added static
  // files are picked up without navigating away. Nothing async to fetch since
  // the catalog is static — but the Refresh flash still gives useful feedback.
  const refresh = async () => {
    setNonce((n) => n + 1);
    // Small settle time so the refreshing spinner has something to animate.
    await new Promise((res) => setTimeout(res, 200));
  };

  return (
    <CollapsibleSection
      id="media-library"
      testId="admin-media-library"
      title={<><ImageSquare size={20} weight="bold" className="text-[#FF1B91] inline mr-2" />Media Library</>}
      subtitle={`Admin-only · ${totalCount} assets across ${CATEGORIES.length} categories · thumbnails link to the existing file on disk.`}
      onRefresh={refresh}
      defaultOpen={false}
    >
      <div className="space-y-4">
        {/* Filter chips */}
        <div className="flex flex-wrap gap-1.5" data-testid="media-filter-chips">
          <button
            type="button"
            onClick={() => setActiveId("all")}
            data-testid="media-filter-all"
            className={`text-[10px] uppercase tracking-[0.2em] font-bold px-3 py-1.5 border ${activeId === "all" ? "bg-[#FF1B91] border-[#FF1B91] text-white" : "border-white/20 text-white/70 hover:border-white/50"}`}
          >
            All ({totalCount})
          </button>
          {CATEGORIES.map((c) => (
            <button
              key={c.id}
              type="button"
              onClick={() => setActiveId(c.id)}
              data-testid={`media-filter-${c.id}`}
              className={`text-[10px] uppercase tracking-[0.2em] font-bold px-3 py-1.5 border inline-flex items-center gap-1.5 ${activeId === c.id ? "bg-[#FF1B91] border-[#FF1B91] text-white" : "border-white/20 text-white/70 hover:border-white/50"}`}
            >
              <c.icon size={11} weight="bold" /> {c.label} ({c.items.length})
            </button>
          ))}
        </div>

        {/* Category sections */}
        {visible.map((c) => (
          <div key={c.id} data-testid={`media-section-${c.id}`}>
            <div className="flex items-center gap-2 mt-2 mb-2">
              <c.icon size={14} weight="fill" className="text-[#FF1B91]" />
              <h3 className="font-display font-black uppercase tracking-tight text-white text-sm">{c.label}</h3>
              <span className="text-[10px] uppercase tracking-[0.25em] text-white/45 font-bold">· {c.items.length} file{c.items.length === 1 ? "" : "s"}</span>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-2">
              {c.items.map((it) => (
                <AssetTile key={it.url} item={it} type={c.type} />
              ))}
            </div>
          </div>
        ))}
      </div>
    </CollapsibleSection>
  );
}
